import { Clinic } from './location.type'

export interface ProviderPhotoFile {
  file_path?: string
}

export interface Provider {
  readonly team?: string | null
  provider_id: string
  user_id: string
  clinic_id: string
  operatory_id?: string
  first_name: string
  middle_name?: string
  last_name: string
  username: string
  gender: string
  date_of_birth: string
  pronouns?: string
  marital_status?: string
  address: string
  city: string
  state: string
  zip: string
  cellphone: string
  email: string
  home_number?: string
  work_contact?: string
  fax_number?: string | null
  state_license_number?: string
  state_where_licensed_issued?: string
  state_license_expiry?: string
  state_rx_id?: string
  dea_number?: string
  tax_id_type?: string
  tax_id_number?: string
  social_security_number?: string
  npi_type?: string
  npi_number?: string
  employment_type?: string
  provider_color: ProviderColor
  photo?: string | null
  photo_file?: ProviderPhotoFile | null
  created_by: string
  updated_by: string
  workingHours?: WorkingSchedule[]
  shift_start_time?: string
  shift_end_time?: string
  is_hidden: boolean
  work_role?: string
  date_time_created_at: string
  date_time_updated_at: string
  operatory_rooms: string
  work_status: string
  same_working_hours: boolean
  clinic?: Clinic
  assigned_clinics?: Pick<Clinic, 'clinic_id' | 'clinic_name'>[]
  last_login?: string
}

export interface ProviderColor {
  header: string
  background: string
  text_color: string
  border_color?: string // CSS variable for border color (-400 variant)
  label: string
}

export interface WorkingSchedule {
  working_hours_id?: string
  entity_type?: 'provider' | 'operatory'
  entity_id?: string
  day_of_week: string
  start_time: string
  end_time: string
}

export enum DayOfWeek {
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
  SATURDAY = 'Saturday',
  SUNDAY = 'Sunday',
}

export type WorkOverrideRow = {
  id: string
  name: string
  date: string
  start_time: string
  end_time: string
}

export interface ProviderData {
  // Basic Information
  providerId?: string
  userId: string
  clinicId: string
  clinicIds?: string[]
  operatoryId?: string

  // Personal Information
  firstName: string
  lastName: string
  middleName?: string
  username: string
  gender: string
  dateOfBirth: string
  pronouns?: string
  maritalStatus?: string

  // Contact Information
  address: string
  city: string
  state: string
  zip: string
  mobile: string
  email: string
  phone?: string // Home number
  work?: string // Work contact
  fax?: string // Fax number

  // Professional Information
  stateLicenseNumber?: string
  stateWhereLicensed?: string
  expiryDate?: string
  stateRxId?: string
  deaNumber?: string
  taxIdType?: string // TIN or SSN
  taxIdNumber?: string
  socialSecurityNumber?: string
  npiType?: string
  npiNumber?: string
  employmentType?: string
  workRole?: string

  // Appearance & UI
  providerColor: ProviderColor

  // Work Schedule
  sameWorkingHours: boolean
  workingSchedules?: WorkingSchedule[]
  shiftStartTime?: string
  shiftEndTime?: string
  workStatus: string // Active, etc.
  workOverrides?: WorkOverrideRow[]

  // Operatory Rooms
  operatoryRooms: string

  // System Fields
  isHidden: boolean
  createdBy: string
  updatedBy: string
  dateTimeCreatedAt: string
  dateTimeUpdatedAt: string
  password?: string
}

export interface Override {
  date: string
  fromTime: string
  toTime: string
}

// Partial types for forms and updates
export type ProviderPersonalInfo = Pick<
  ProviderData,
  | 'firstName'
  | 'lastName'
  | 'middleName'
  | 'username'
  | 'gender'
  | 'dateOfBirth'
  | 'pronouns'
  | 'maritalStatus'
  | 'address'
  | 'city'
  | 'state'
  | 'zip'
  | 'mobile'
  | 'email'
  | 'phone'
  | 'work'
  | 'fax'
  | 'workRole'
>

export type ProviderWorkInfo = Pick<
  ProviderData,
  | 'stateLicenseNumber'
  | 'stateWhereLicensed'
  | 'expiryDate'
  | 'stateRxId'
  | 'deaNumber'
  | 'taxIdType'
  | 'taxIdNumber'
  | 'socialSecurityNumber'
  | 'npiType'
  | 'npiNumber'
  | 'employmentType'
  | 'providerColor'
  | 'sameWorkingHours'
  | 'workingSchedules'
  | 'workOverrides'
  | 'workStatus'
  | 'shiftStartTime'
  | 'shiftEndTime'
  | 'operatoryId'
  | 'workRole'
  | 'taxIdType'
  | 'clinicId'
  | 'clinicIds'
>

export type ProviderBasicInfo = Pick<
  ProviderData,
  | 'providerId'
  | 'firstName'
  | 'lastName'
  | 'middleName'
  | 'username'
  | 'email'
  | 'mobile'
  | 'providerColor'
  | 'isHidden'
>

// Form data types for modals and forms
export type ProviderFormData = Partial<ProviderData>

// Validation error types
export interface ProviderValidationErrors {
  [key: string]: string
}

// API response types
export interface ProviderApiResponse {
  success: boolean
  data?: ProviderData
  message?: string
  errors?: ProviderValidationErrors
}

export interface ProviderListResponse {
  success: boolean
  data?: ProviderData[]
  total?: number
  page?: number
  limit?: number
  message?: string
}

// Utility types for component props
export interface ProviderComponentProps {
  providerData: ProviderData
  onUpdate?: (updatedData: Partial<ProviderData>) => void
  onDelete?: (providerId: string) => void
  validationErrors?: ProviderValidationErrors
  isLoading?: boolean
  isReadOnly?: boolean
}

// Search and filter types
export interface ProviderSearchFilters {
  searchTerm?: string
  specialty?: string
  workStatus?: string
  isHidden?: boolean
  clinicId?: string
  operatoryId?: string
}

export interface ProviderSortOptions {
  field: keyof ProviderData
  direction: 'asc' | 'desc'
}

// Type guards
export const isProviderData = (obj: unknown): obj is ProviderData => {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    'providerId' in obj &&
    'firstName' in obj &&
    'lastName' in obj &&
    'email' in obj &&
    typeof (obj as Record<string, unknown>).providerId === 'string' &&
    typeof (obj as Record<string, unknown>).firstName === 'string' &&
    typeof (obj as Record<string, unknown>).lastName === 'string' &&
    typeof (obj as Record<string, unknown>).email === 'string'
  )
}

export const isProviderPersonalInfo = (
  obj: unknown,
): obj is ProviderPersonalInfo => {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    'firstName' in obj &&
    'lastName' in obj &&
    'email' in obj &&
    typeof (obj as Record<string, unknown>).firstName === 'string' &&
    typeof (obj as Record<string, unknown>).lastName === 'string' &&
    typeof (obj as Record<string, unknown>).email === 'string'
  )
}
